import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { eSignatureService } from "../../services/esignature/ESignatureService";
import ModalNew from "./popup/Modal";
import PopupHeader from "./popup/common/popupHeader";
import LoadingPlaceholder from "./loading/LoadingPlaceholder";

/**
 * Reusable E-Signature Component
 * Handles document signing for any document type
 *
 * @param {Object} props
 * @param {string} props.documentType - Type of document to sign (W9, TAXPAYER_AUTH, etc.)
 * @param {string} [props.filingUuid] - Optional filing UUID
 * @param {string|number} [props.userId] - Optional user ID
 * @param {Function} props.onSuccess - Optional callback when signing is completed
 * @param {Function} props.onError - Optional callback when error occurs
 * @param {JSX.Element} props.customContent - Custom content to display before signing
 * @param {string} props.customSuccessMessage - Optional custom success message
 * @param {string} props.customErrorTitle - Optional custom error title
 * @param {string} props.successButtonText - Text for success button (default: "Continue")
 * @param {string} props.successButtonAction - Action for success button (default: navigate to home)
 * @param {Function} props.onCompleted - Required callback for completed event (handles document-specific logic)
 */
function ESignatureComponent({
  documentType,
  filingUuid,
  userId,
  onSuccess,
  onError,
  customContent,
  customSuccessMessage,
  customErrorTitle,
  successButtonText = "Continue",
  successButtonAction,
  onCompleted,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signingSession, setSigningSession] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const initializeSigning = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create signing session
      const session = await eSignatureService.createSigningSession({
        documentType: documentType,
        filingUuid: filingUuid,
        userId: userId,
      });

      setSigningSession(session);

      // Use the SignWell signing_url from the backend response
      if (session.signing_url) {
        // SignWell SDK will create its own popup/modal for signing

        // Initialize SignWell embedded signing after DOM is ready
        setTimeout(() => {
          if (window.SignWellEmbed) {
            const signWellEmbed = new window.SignWellEmbed({
              url: session.signing_url,
              // SignWell creates its own popup/modal, these options may not work
              // but we can try them for any potential customization
              events: {
                completed: function (e) {
                  // Call custom onCompleted callback if provided
                  if (onCompleted) {
                    onCompleted(e);
                  }

                  // Show success popup (common for all document types)
                  setShowSuccessPopup(true);
                  setLoading(false);

                  // Call custom success callback if provided
                  if (onSuccess) {
                    onSuccess(e);
                  }
                },
                closed: function (e) {
                  // Reset to initial state when popup is closed
                  setSigningSession(null);
                  setLoading(false);
                },
                loaded: function (e) {
                  // Keep loading state while iframe is open
                  setLoading(true);
                },
                error: function (e) {
                  setError(
                    "Signing interface failed to load. Please try again.",
                  );
                  setShowErrorPopup(true);
                  setLoading(false);
                  // Call custom error callback if provided
                  if (onError) {
                    onError(e);
                  }
                },
              },
            });
            signWellEmbed.open();
          } else {
            setError("SignWell SDK not available. Please refresh the page.");
          }
        }, 100);
      } else {
        setError("SignWell signing URL not provided by backend.");
      }
    } catch (err) {
      setError(err.message);
      setShowErrorPopup(true);
      // Call custom error callback if provided
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessButtonClick = () => {
    setShowSuccessPopup(false);
    setSigningSession(null);

    if (successButtonAction) {
      successButtonAction();
    } else {
      // Default action: navigate to home page
      navigate("/");
    }
  };

  const handleErrorButtonClick = () => {
    setShowErrorPopup(false);
    setError(null);
    setSigningSession(null);
  };

  return (
    <Fragment>
      {loading && (
        <div className="text-center">
          <LoadingPlaceholder />
          <p>
            {signingSession
              ? "SignWell signing interface is ready..."
              : "Preparing document for signing..."}
          </p>
        </div>
      )}

      {!loading && !error && !signingSession && (
        <div>
          {customContent}
          <div>
            <br />
            <button
              className="cta-button primary"
              onClick={initializeSigning}
              data-icon="arrow_forward"
              disabled={loading}
            >
              <b>Start {documentType} Signing Process</b>
            </button>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <ModalNew>
          <PopupHeader
            title={customErrorTitle || "Signing Error"}
            setOpenState={setShowErrorPopup}
          />
          <div style={{ padding: "20px 0" }}>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              {error ||
                "An error occurred while preparing your document for signing."}
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="cta-button primary"
                onClick={handleErrorButtonClick}
                style={{ margin: "0 auto", marginTop: "40px" }}
              >
                <b>Try Again</b>
              </button>
            </div>
          </div>
        </ModalNew>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <ModalNew>
          <PopupHeader
            title="Signed Successfully!"
            setOpenState={setShowSuccessPopup}
          />
          <div style={{ padding: "20px 0" }}>
            <div
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#28a745",
                fontSize: "48px",
              }}
            >
              ✓
            </div>
            <p
              style={{
                textAlign: "center",
              }}
            >
              {customSuccessMessage ||
                "W-9 Submitted — You’re all set! Your W-9 form has been signed and saved securely."}
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                className="cta-button primary"
                onClick={handleSuccessButtonClick}
                style={{ margin: "0 auto", marginTop: "40px" }}
                data-icon="arrow_forward"
              >
                <b>{successButtonText}</b>
              </button>
            </div>
          </div>
        </ModalNew>
      )}
    </Fragment>
  );
}

export default ESignatureComponent;
